/**
 * V68 인력 관리 — Tab 2: 유휴인력 관리
 * 비고 컬럼: 평소엔 자동/수동 비고 텍스트만 표시, 클릭 시 모달로 편집.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useIdleWorkforce, useUpdateIdleWorkforceNote } from '../../../shared/hooks/useIdleWorkforce';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';
// V68: 고용형태 한글 표시 공용 formatter
import { formatEmploymentTypeName } from '../../../shared/utils/employmentTypeFormatter';

function getCurrentYearMonth(): string {
  const kst = new Date(new Date().getTime() + 9 * 60 * 60 * 1000);
  return `${kst.getUTCFullYear()}-${String(kst.getUTCMonth() + 1).padStart(2, '0')}`;
}

function generateYearMonthOptions(): string[] {
  const cur = getCurrentYearMonth();
  const [y, m] = cur.split('-').map(Number);
  const arr: string[] = [];
  for (let i = -6; i <= 6; i++) {
    const d = new Date(Date.UTC(y, m - 1 + i, 1));
    arr.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return arr;
}

const cellBase: React.CSSProperties = {
  padding: '10px', borderBottom: '1px solid var(--color-gray-200)', verticalAlign: 'middle', fontSize: 13,
};
const centerCell: React.CSSProperties = {
  ...cellBase, textAlign: 'center',
};
const headerCell: React.CSSProperties = {
  padding: '10px', background: 'var(--color-gray-100)', borderBottom: '2px solid var(--color-gray-300)',
  fontWeight: 600, fontSize: 13, textAlign: 'center',
};

interface EditTarget {
  userId: string;
  nameKo: string;
  autoRemark: string;
  manualNote: string;
}

function NoteCellDisplay({ autoRemark, manualNote, onClick }: {
  autoRemark: string; manualNote: string; onClick: () => void;
}) {
  const empty = !autoRemark && !manualNote;
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{
        cursor: 'pointer', minHeight: 28, padding: '4px 6px', borderRadius: 4,
        display: 'flex', flexDirection: 'column', gap: 4,
      }}
      title="클릭하여 비고 작성"
    >
      {autoRemark && (
        <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
          [자동] {autoRemark}
        </div>
      )}
      {manualNote && (
        <div style={{ fontSize: 13, color: 'var(--color-gray-800)', whiteSpace: 'pre-wrap' }}>
          {manualNote}
        </div>
      )}
      {empty && (
        <div style={{ fontSize: 12, color: 'var(--color-gray-400)' }}>
          클릭하여 작성
        </div>
      )}
    </div>
  );
}

function NoteEditModal({ yearMonth, target, onClose, onSaved }: {
  yearMonth: string;
  target: EditTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(target.manualNote);
  const { save, saving, error } = useUpdateIdleWorkforceNote();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onSave = async () => {
    const ok = await save(yearMonth, target.userId, draft);
    if (ok) { onSaved(); onClose(); }
  };

  return (
    <ModalOverlay
      onClose={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8, padding: 20, width: 480, maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600 }}>{target.nameKo} 비고 작성</div>

        {target.autoRemark && (
          <div>
            <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 4 }}>자동 비고 (수정 불가)</div>
            <div style={{
              padding: '8px 10px', background: 'var(--color-gray-50)', borderRadius: 4,
              borderLeft: '3px solid var(--color-blue-300)', fontSize: 13, color: 'var(--color-gray-700)',
            }}>
              {target.autoRemark}
            </div>
          </div>
        )}

        <div>
          <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginBottom: 4 }}>수동 비고</div>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            maxLength={500}
            rows={5}
            autoFocus
            style={{
              width: '100%', padding: 8, border: '1px solid var(--color-gray-300)', borderRadius: 4,
              fontSize: 13, resize: 'vertical', minHeight: 100, boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 11, color: 'var(--color-gray-500)', textAlign: 'right' }}>
            {draft.length} / 500
          </div>
        </div>

        {error && <div style={{ fontSize: 12, color: 'var(--color-red-600)' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 16px', border: '1px solid var(--color-gray-300)', borderRadius: 4,
              background: 'white', color: 'var(--color-gray-700)', cursor: 'pointer', fontSize: 13,
            }}
          >취소</button>
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 4,
              background: 'var(--color-primary)', color: 'white',
              cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            완료
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

export function IdleWorkforceTab() {
  const [yearMonth, setYearMonth] = useState<string>(getCurrentYearMonth());
  const { data, yearMonth: resolvedYm, loading, error, refresh } = useIdleWorkforce(yearMonth);
  const options = useMemo(() => generateYearMonthOptions(), []);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <label style={{ fontSize: 14 }}>월:</label>
        <select value={yearMonth} onChange={e => setYearMonth(e.target.value)}
          style={{ padding: '6px 12px', border: '1px solid var(--color-gray-300)', borderRadius: 6, fontSize: 14 }}>
          {options.map(o => <option key={o} value={o}>{o.replace('-', '년 ')}월</option>)}
        </select>
        <span style={{ fontSize: 13, color: 'var(--color-gray-600)' }}>유휴 직원: {data.length}명</span>
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center' }}><Loader2 size={24} className="animate-spin" /></div>}
      {error && <div style={{ padding: 20, background: 'var(--color-red-50)', color: 'var(--color-red-700)', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      {!loading && !error && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 180 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 100 }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th style={headerCell}>부서</th>
                <th style={headerCell}>이름</th>
                <th style={headerCell}>고용형태</th>
                <th style={headerCell}>비고</th>
              </tr>
            </thead>
            <tbody>
              {data.map(row => {
                const orgTitle = `${row.orgUnitName} (${row.companyCode})`;
                return (
                <tr key={row.userId}>
                  <td style={{
                    ...centerCell,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={orgTitle}>
                    {row.orgUnitName}
                    <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--color-gray-500)' }}>({row.companyCode})</span>
                  </td>
                  <td style={centerCell}>
                    {row.nameKo}
                    {row.status === 'on_leave' && (
                      <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--color-orange-600)' }}>(휴직)</span>
                    )}
                  </td>
                  <td style={centerCell}>{formatEmploymentTypeName(row.employmentTypeCode, row.employmentTypeName)}</td>
                  <td style={cellBase}>
                    <NoteCellDisplay
                      autoRemark={row.autoRemark}
                      manualNote={row.manualNote}
                      onClick={() => setEditTarget({
                        userId: row.userId,
                        nameKo: row.nameKo,
                        autoRemark: row.autoRemark,
                        manualNote: row.manualNote,
                      })}
                    />
                  </td>
                </tr>
                );
              })}
              {data.length === 0 && (
                <tr><td colSpan={4} style={{ ...cellBase, textAlign: 'center', padding: 40, color: 'var(--color-gray-500)' }}>
                  유휴 직원이 없습니다.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editTarget && (
        <NoteEditModal
          yearMonth={resolvedYm || yearMonth}
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
